import { Spin } from 'antd';
import Button from 'components/common/Button';
import {
  ROTATION_DELETE_FAILED_MESSAGE,
  ROTATION_PASTE_CLEARS_SOWING_MESSAGE,
  ROTATION_PASTE_DATE_SHIFT_MESSAGE,
  ROTATION_PASTE_FAILED_MESSAGE,
  ROTATION_PASTE_OTHER_TASKS_MESSAGE,
  ROTATION_PASTE_OUTSIDE_MESSAGE,
  ROTATION_PASTE_REPLACES_MIX_MESSAGE,
  ROTATION_PASTE_TOO_MANY_MESSAGE,
} from 'constants/messageConstants';
import {
  ROTATION_CANCEL_LABEL,
  ROTATION_DELETE_DONE,
  ROTATION_DELETE_PROGRESS,
  ROTATION_PASTE_APPLY_LABEL,
  ROTATION_PASTE_CELL_LABEL,
  ROTATION_PASTE_DETAILS_LABEL,
  ROTATION_PASTE_DISMISS_LABEL,
  ROTATION_PASTE_DONE,
  ROTATION_PASTE_MAX_CELLS,
  ROTATION_PASTE_NEW_COUNT,
  ROTATION_PASTE_NO_SOWING_LINE,
  ROTATION_PASTE_SOWING_LINE,
  ROTATION_PASTE_PROGRESS,
  ROTATION_PASTE_RETRY_LABEL,
  ROTATION_PASTE_SKIPPED_COUNT,
  ROTATION_PASTE_SUMMARY,
  ROTATION_PASTE_UPDATE_COUNT,
} from 'constants/uiConstants';
import React, { useState } from 'react';
import { LuCheck, LuChevronDown, LuClipboard } from 'react-icons/lu';
import RotationAnchoredPanel from './RotationAnchoredPanel';
import { formatSowingDetails } from './rotationFormat';
import {
  valuesHaveSowing,
  type GridPosition,
  type RotationPastePlan,
} from './rotationPaste';

/** Skipped cells listed by name; the rest are counted. */
const SKIPPED_LIST_MAX = 5;

/** What a run of saves is doing — pasting crops, or deleting plans. */
export type RotationBulkVerb = 'paste' | 'delete';

export type RotationPasteState =
  | { kind: 'preview'; plan: RotationPastePlan }
  | { kind: 'applying'; verb: RotationBulkVerb; done: number; total: number }
  | { kind: 'done'; verb: RotationBulkVerb; saved: number; failed: number };

interface RotationPasteBarProps {
  state: RotationPasteState;
  /** The grid's scroll area, which the bar stays inside. */
  containerRef: React.RefObject<HTMLElement>;
  /** The cells being pasted to, skipped or deleted — the bar sits beside them. */
  positions: GridPosition[];
  /** Shown while saving when another paste or delete was asked for. */
  busyNotice: string | null;
  onApply: () => void;
  onCancel: () => void;
  onRetry: () => void;
  onDismiss: () => void;
}

/**
 * The paste preview, and the progress and result of a paste or delete
 * (OP-2501), pinned beside the cells it acts on while the grid shows the
 * paste as drafts. Before anything is saved it says what the paste will do
 * in one line — new plans, updates and skips — with the rest (sowing, each
 * skip's reason, notes) under Details. Apply (or Enter) saves; Cancel (or
 * Escape) leaves the grid as it was. While saving it shows progress, then
 * how many saved, with Retry for any that didn't.
 */
const RotationPasteBar: React.FC<RotationPasteBarProps> = ({
  state,
  containerRef,
  positions,
  busyNotice,
  onApply,
  onCancel,
  onRetry,
  onDismiss,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (state.kind === 'preview') onCancel();
      else if (state.kind === 'done') onDismiss();
    }
  };

  const renderBody = () => {
    if (state.kind === 'applying') {
      return (
        <div className="flex flex-col gap-1.5">
          <p className="flex items-center gap-2 text-sm text-charcoal">
            <Spin size="small" />
            {(state.verb === 'delete'
              ? ROTATION_DELETE_PROGRESS
              : ROTATION_PASTE_PROGRESS)(state.done, state.total)}
          </p>
          {busyNotice && <p className="text-sm text-charcoal">{busyNotice}</p>}
        </div>
      );
    }

    if (state.kind === 'done') {
      return (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <p className="flex items-center gap-1.5 text-sm text-charcoal">
            {state.failed === 0 && <LuCheck size={16} aria-hidden />}
            {state.saved > 0 &&
              (state.verb === 'delete'
                ? ROTATION_DELETE_DONE
                : ROTATION_PASTE_DONE)(state.saved)}
            {state.saved > 0 && state.failed > 0 && '. '}
            {state.failed > 0 &&
              (state.verb === 'delete'
                ? ROTATION_DELETE_FAILED_MESSAGE
                : ROTATION_PASTE_FAILED_MESSAGE)(state.failed)}
          </p>
          <span className="flex flex-shrink-0 gap-2">
            {state.failed > 0 && (
              <Button
                type="button"
                variant="success"
                size="sm"
                className="px-3"
                onClick={onRetry}
              >
                {ROTATION_PASTE_RETRY_LABEL}
              </Button>
            )}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="px-3"
              onClick={onDismiss}
            >
              {ROTATION_PASTE_DISMISS_LABEL}
            </Button>
          </span>
        </div>
      );
    }

    const { plan } = state;
    const created = plan.targets.filter((t) => !t.inputId).length;
    const updated = plan.targets.length - created;
    const withOtherTasks = plan.targets.filter((t) => t.hasOtherTasks).length;
    const counts = [
      created > 0 ? ROTATION_PASTE_NEW_COUNT(created) : null,
      updated > 0 ? ROTATION_PASTE_UPDATE_COUNT(updated) : null,
      plan.skipped.length > 0
        ? ROTATION_PASTE_SKIPPED_COUNT(plan.skipped.length)
        : null,
    ].filter(Boolean);
    const clearsSowing = plan.targets.some(
      (t) => t.inputId && !valuesHaveSowing(t.values)
    );
    const movesDate = plan.targets.some((t) => t.values.sowing.date);
    // A blank paste onto a mix is covered by the cleared-sowing note.
    const replacesMix = plan.targets.filter(
      (t) => t.replacesMix && valuesHaveSowing(t.values)
    ).length;
    const notes = [
      withOtherTasks > 0
        ? ROTATION_PASTE_OTHER_TASKS_MESSAGE(withOtherTasks)
        : null,
      plan.outside > 0 ? ROTATION_PASTE_OUTSIDE_MESSAGE(plan.outside) : null,
      movesDate ? ROTATION_PASTE_DATE_SHIFT_MESSAGE : null,
      replacesMix > 0 ? ROTATION_PASTE_REPLACES_MIX_MESSAGE(replacesMix) : null,
      clearsSowing ? ROTATION_PASTE_CLEARS_SOWING_MESSAGE : null,
    ].filter((note): note is string => !!note);
    const canApply = plan.targets.length > 0 && !plan.tooMany;
    // One copied crop: say exactly which sowing goes with it. A block's
    // crops each carry their own.
    const { clipboard } = plan;
    const isSingle = clipboard.width === 1 && clipboard.height === 1;
    const sowingDetails = isSingle
      ? formatSowingDetails(clipboard.entries[0].values.sowing, true)
      : null;

    const hasDetails = isSingle || plan.skipped.length > 0 || notes.length > 0;

    return (
      <div className="flex flex-col gap-1.5">
        <p className="flex min-w-0 items-start gap-1.5 text-sm text-charcoal">
          <LuClipboard size={16} aria-hidden className="mt-0.5 flex-shrink-0" />
          <span>
            <span className="font-semibold">
              {ROTATION_PASTE_SUMMARY(
                plan.clipboard.label,
                plan.targets.length
              )}
            </span>
            {counts.length > 0 && `: ${counts.join(' · ')}`}
          </span>
        </p>
        {plan.tooMany && (
          <p className="text-sm text-danger-dark">
            {ROTATION_PASTE_TOO_MANY_MESSAGE(
              ROTATION_PASTE_MAX_CELLS,
              plan.targets.length
            )}
          </p>
        )}
        {hasDetails && showDetails && (
          <div className="flex flex-col gap-1.5">
            {isSingle && (
              <p className="text-sm text-charcoal">
                {sowingDetails
                  ? ROTATION_PASTE_SOWING_LINE(sowingDetails)
                  : ROTATION_PASTE_NO_SOWING_LINE}
              </p>
            )}
            {plan.skipped.length > 0 && (
              <ul className="text-sm text-gray-dark">
                {plan.skipped.slice(0, SKIPPED_LIST_MAX).map((skip) => (
                  <li key={skip.key}>
                    {ROTATION_PASTE_CELL_LABEL(skip.paddockName, skip.season)} —{' '}
                    {skip.reason}
                  </li>
                ))}
                {plan.skipped.length > SKIPPED_LIST_MAX && (
                  <li>
                    {ROTATION_PASTE_SKIPPED_COUNT(
                      plan.skipped.length - SKIPPED_LIST_MAX
                    )}
                  </li>
                )}
              </ul>
            )}
            {notes.map((note) => (
              <p key={note} className="text-sm text-gray-dark">
                {note}
              </p>
            ))}
          </div>
        )}
        <span className="flex flex-wrap items-center justify-end gap-2">
          {hasDetails && (
            <button
              type="button"
              aria-expanded={showDetails}
              className="mr-auto flex items-center gap-1 rounded px-1 text-sm font-semibold text-gray-dark hover:bg-rowHover focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
              onClick={() => setShowDetails((open) => !open)}
            >
              {ROTATION_PASTE_DETAILS_LABEL}
              {/* Notes are counted, so a closed Details still says there's more to read. */}
              {notes.length > 0 && ` (${notes.length})`}
              <LuChevronDown
                size={14}
                aria-hidden
                className={showDetails ? 'rotate-180' : undefined}
              />
            </button>
          )}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="px-3"
            onClick={onCancel}
          >
            {ROTATION_CANCEL_LABEL}
          </Button>
          {canApply && (
            <Button
              // Enter applies straight away, as in a confirm dialog.
              autoFocus
              type="button"
              variant="success"
              size="sm"
              className="px-3"
              onClick={onApply}
            >
              {ROTATION_PASTE_APPLY_LABEL}
            </Button>
          )}
        </span>
      </div>
    );
  };

  return (
    <RotationAnchoredPanel
      containerRef={containerRef}
      positions={positions}
      onKeyDown={handleKeyDown}
    >
      {renderBody()}
    </RotationAnchoredPanel>
  );
};

export default RotationPasteBar;
